/**
 * <fd-account>: the Fandom account pop-up content (snippets/fd-account-panel.liquid).
 *
 * - The Wishlist tile and row (`[data-fd-wishlist-open]`) are opened by assets/fd-wishlist.js, whose click listener on
 *   the document opens ReStock's drawer. This element's own click listener runs first (the event reaches it before the
 *   document) and closes the pop-up around it, so the drawer isn't left behind it: the desktop popover with
 *   hidePopover(), the phone sheet with its dialog-component's closeDialog(). The event is never stopped.
 * - Closing the pop-up hands focus back to the account icon, behind ReStock's drawer: once the pop-up is closed (the
 *   sheet slides out first) and the drawer is open, focus moves into the drawer.
 * - fd-wishlist.js writes the count into every `[data-fd-wishlist-count]`, but only hides the ones inside
 *   <fd-wishlist>: the badge here is hidden while its number is 0 (or missing).
 */

const OPENER = '[data-fd-wishlist-open]';
const COUNT = '[data-fd-wishlist-count]';
const DRAWER = '.nm-wishlist-drawer';
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
/** How long to wait for ReStock to open its drawer after the click. */
const DRAWER_WAIT_MS = 2000;

/** @param {Element} badge */
function syncBadge(badge) {
  const n = parseInt(badge.textContent || '', 10);
  const hide = !(n > 0);
  if (badge instanceof HTMLElement && badge.hidden !== hide) badge.hidden = hide;
}

/**
 * ReStock's drawer while it is open. It stays in the page when closed; its .nm-modal-container then takes no pointer
 * events (inline style, set back to auto as it opens).
 * @returns {HTMLElement | null}
 */
function openDrawer() {
  const drawer = document.querySelector(DRAWER);
  if (!(drawer instanceof HTMLElement) || !drawer.getClientRects().length) return null;
  const style = getComputedStyle(drawer.closest('.nm-modal-container') || drawer);
  return style.pointerEvents === 'none' || style.visibility === 'hidden' ? null : drawer;
}

/** Moves focus into ReStock's drawer (its first visible control, else the drawer itself) as soon as it is open. */
function focusDrawer() {
  const until = performance.now() + DRAWER_WAIT_MS;
  const attempt = () => {
    const drawer = openDrawer();
    if (!drawer) {
      if (performance.now() < until) window.setTimeout(attempt, 50);
      return;
    }
    if (drawer.contains(document.activeElement)) return;
    const first = [...drawer.querySelectorAll(FOCUSABLE)].find(
      (el) => el instanceof HTMLElement && el.getClientRects().length > 0
    );
    if (first instanceof HTMLElement) {
      first.focus({ preventScroll: true });
      return;
    }
    if (!drawer.hasAttribute('tabindex')) drawer.setAttribute('tabindex', '-1');
    drawer.focus({ preventScroll: true });
  };
  attempt();
}

/** @param {Element} from */
function closePopup(from) {
  const popover = from.closest('[popover]');
  if (popover instanceof HTMLElement && typeof popover.hidePopover === 'function') {
    try {
      if (popover.matches(':popover-open')) popover.hidePopover();
    } catch (error) {
      // Not open, or no :popover-open support: nothing to close.
    }
    // ReStock opens its drawer after this click has reached the document.
    window.setTimeout(focusDrawer, 0);
    return;
  }
  const dialog = /** @type {(HTMLElement & { closeDialog?: () => unknown }) | null} */ (from.closest('dialog-component'));
  if (!dialog || typeof dialog.closeDialog !== 'function') return;
  // The modal sheet makes the rest of the page inert until it has slid out and closed; closing it then returns focus
  // to the account icon. Move focus on from there.
  const modal = from.closest('dialog');
  if (modal instanceof HTMLDialogElement && modal.open) modal.addEventListener('close', focusDrawer, { once: true });
  dialog.closeDialog();
}

class FdAccount extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    const badges = this.querySelectorAll(COUNT);
    if (badges.length) {
      this.#observer = new MutationObserver(() => badges.forEach(syncBadge));
      badges.forEach((badge) => {
        syncBadge(badge);
        this.#observer?.observe(badge, { childList: true, characterData: true, subtree: true });
      });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const opener = target && target.closest(OPENER);
    if (opener && this.contains(opener)) closePopup(this);
  };
}

if (!customElements.get('fd-account')) customElements.define('fd-account', FdAccount);
